import axios from "axios";
import moment from "moment";
import Bottleneck from "bottleneck";
import { baseAxios } from "../apis";

export function formatCurrency(num) {
  if (num === null || num === undefined || isNaN(Number(num))) {
    return "0.00";
  }

  const nm = Number(num);
  return nm
    .toFixed(2)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatCrypto(value, precision = 8, locale = "en-US") {
  const num = Number(value);

  if (value === null || value === undefined || isNaN(num)) {
    return "0";
  }

  const factor = Math.pow(10, precision);
  const floored = Math.floor(num * factor) / factor;

  return floored
    .toLocaleString(locale, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
    .replace(/\.?0+$/, ""); // Optional: remove trailing zeros
}

export const getRandomColor = () =>
  `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0")}`;

export function formatMinutes(minutes, short = true) {
  const days = Math.floor(minutes / (60 * 24));
  const hours = Math.floor((minutes % (60 * 24)) / 60);
  const mins = minutes % 60;

  const parts = [];

  if (short) {
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
  } else {
    if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
    if (mins > 0 || parts.length === 0)
      parts.push(`${mins} ${mins === 1 ? "minute" : "minutes"}`);
  }

  return parts.join(" ");
}

export const extractErrorMessage = (errorObj) => {
  // Helper: check if a string contains HTML tags
  const containsHTML = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

  // If error is a string
  if (typeof errorObj === "string") {
    return containsHTML(errorObj)
      ? "Something went wrong. Please try again later."
      : errorObj;
  }

  // If error is an array (like ['message 1', 'message 2'])
  if (Array.isArray(errorObj)) {
    return (
      errorObj.filter((e) => typeof e === "string").join(", ") ||
      extractErrorMessage(errorObj[0])
    );
  }

  // If error is an HTML element
  if (
    typeof HTMLElement === "object"
      ? errorObj instanceof HTMLElement
      : errorObj &&
        typeof errorObj === "object" &&
        errorObj !== null &&
        errorObj.nodeType === 1 &&
        typeof errorObj.nodeName === "string"
  ) {
    return "Something went wrong. Please try again later.";
  }

  // If error is an object, recurse its values
  if (typeof errorObj === "object" && errorObj !== null) {
    for (const key in errorObj) {
      const message = extractErrorMessage(errorObj[key]);
      if (message) return message;
    }
  }

  // This part of the function handles the error you provided
  if (errorObj?.message) {
    return errorObj.message;
  }

  // Default fallback message
  return "Something went wrong. Please try again later.";
};

export const formatOrderData = (order) => ({
  id: order.id,
  datetime: moment(order.created_at).format("DD-MM-YY HH:mm:ss"),
  pair: order.symbol.replace("USDT", "/USDT"),
  type: order.order_type.charAt(0).toUpperCase() + order.order_type.slice(1),
  side: order.side.charAt(0).toUpperCase() + order.side.slice(1),
  price: parseFloat(order.price).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  amount: parseFloat(order.qty).toFixed(8),
  total: parseFloat(order.usdt_qty).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
});

export const transformNotifications = (notifications) => {
  const exchangeMap = {
    1: "Binance",
    2: "OKX",
  };
  const grouped = {};

  notifications.forEach((item) => {
    const dateObj = new Date(item.created_at);
    const formattedDate = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    const formattedTimestamp =
      dateObj.toLocaleDateString("en-GB") +
      " " +
      dateObj.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

    const notif = {
      pair: item.asset_pair,
      bot: item.bot_name?.toUpperCase() || null,
      exchange: exchangeMap[item.exchange] || "Unknown",
      message: item.description,
      timestamp: moment(dateObj).format("DD/MM/YYYY HH:mm"),
    };

    if (!grouped[formattedDate]) {
      grouped[formattedDate] = [];
    }

    grouped[formattedDate].push(notif);
  });

  return Object.entries(grouped).map(([date, notifications]) => ({
    date,
    notifications,
  }));
};

export default function imageToFile(blob, filename, mimeType = "image/jpeg") {
  return new Promise((resolve) => {
    const file = new File([blob], filename, { type: mimeType });
    resolve(file);
  });
}

export const getSafeNumericValue = (value, max = Infinity) => {
  const numericString = String(value)
    .replace(/[^0-9.]/g, "")
    .replace(/(\..*?)\..*/g, "$1"); // Remove extra dots

  const number = parseFloat(numericString);
  if (isNaN(number)) return 0;

  return Math.min(number, max);
};

/**
 * Checks if a string is a valid decimal input (including in-progress ones like "123." or ".5")
 */
export const isValidDecimalInput = (value) => {
  return /^\d*\.?\d*$/.test(value);
};

/**
 * Checks if the input is an in-progress decimal (like "123." or ".")
 */
export const isInProgressDecimal = (value) => {
  return /^\d*\.$/.test(value) || /^\.\d*$/.test(value);
};

export const limitDecimalPlaces = (value, maxDecimals = 8) => {
  if (value === "" || value === null || value === undefined) return value;

  const str = value.toString();
  // if there's a decimal point, trim after 8 places
  if (str.includes(".")) {
    const [intPart, decPart] = str.split(".");
    return `${intPart}.${decPart.slice(0, maxDecimals)}`;
  }
  return str;
};

export const sanitizeNumericInput = (
  raw,
  maxDecimals = 8,
  allowNegative = false,
) => {
  if (raw === "" || raw === null || raw === undefined) return "";

  // Always treat as string
  let str = String(raw);

  // 0. Normalise locale: convert commas to dots
  // str = str.replace(/,/g, ".");

  // 1. Remove every character except digits, dot and minus (minus only if allowed)
  if (allowNegative) {
    str = str.replace(/[^0-9\.\-]/g, "");
  } else {
    str = str.replace(/[^0-9\.]/g, "");
  }

  // 2. Minus sign: keep only at the very start
  if (allowNegative) {
    str = str.replace(/(?!^)-/g, "");
  } else {
    str = str.replace(/-/g, "");
  }

  // 3. Allow only one dot
  const firstDot = str.indexOf(".");
  if (firstDot !== -1) {
    const before = str.slice(0, firstDot);
    const after = str.slice(firstDot + 1).replace(/\./g, ""); // remove any other dots
    str = `${before}.${after}`;
  }

  // 4. Limit decimals
  if (str.includes(".")) {
    const [intPart, decPart] = str.split(".");
    str = `${intPart}.${decPart.slice(0, maxDecimals)}`;
  }

  return str;
};
// --- Rate Limiter Configuration ---
const limiter = new Bottleneck({
  maxConcurrent: 2, // Allow 2 concurrent requests
  minTime: 500, // Wait at least 500ms between each request
});

// Wrap axios.get with rate limiter
const rateLimitedAxiosGet = limiter.wrap(axios.get);
const rateLimitedBaseAxiosGet = limiter.wrap(baseAxios.get);

// --- Two-Layer Cache ---
const coinCache = new Map();

const getFromLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    const parsed = JSON.parse(item);

    // Check if cache is expired (24 hours)
    const now = Date.now();
    if (parsed.expiry && now > parsed.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.error("Error reading from localStorage:", error);
    return null;
  }
};

const saveToLocalStorage = (key, data) => {
  try {
    const item = {
      data,
      expiry: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
};

// --- Queue for batch processing ---
const pendingRequests = new Map();
let batchTimeout = null;

/**
 * Batch fetch multiple coin images at once - ONE REQUEST PER SYMBOL
 * @param {string[]} symbols - Array of coin symbols
 * @returns {Promise<Object>} - Object mapping symbols to image URLs
 */
const batchFetchCoins = async (symbols) => {
  if (!symbols || symbols.length === 0) return {};

  const uniqueSymbols = [...new Set(symbols.map((s) => s.toLowerCase()))];
  const results = {};

  // Filter out symbols that are already cached
  const uncachedSymbols = uniqueSymbols.filter((symbol) => {
    const cacheKey = `coin_image_${symbol}`;

    // Check in-memory cache
    if (coinCache.has(symbol)) {
      results[symbol] = coinCache.get(symbol);
      return false;
    }

    // Check localStorage cache
    const localImage = getFromLocalStorage(cacheKey);
    if (localImage) {
      coinCache.set(symbol, localImage);
      results[symbol] = localImage;
      return false;
    }

    return true;
  });

  if (uncachedSymbols.length === 0) {
    return results;
  }

  // Process each symbol individually (backend doesn't support batch)
  for (const symbol of uncachedSymbols) {
    try {
      const backendRes = await rateLimitedBaseAxiosGet(`/api/get_coin_image`, {
        params: {
          symbol: symbol.toUpperCase(),
        },
        timeout: 5000, // 5 second timeout
      });

      // Process backend response - single object
      if (backendRes.data && backendRes.data.image_url) {
        const img = backendRes.data.image_url;
        results[symbol] = img;
        coinCache.set(symbol, img);
        saveToLocalStorage(`coin_image_${symbol}`, img);
      } else {
        results[symbol] = null;
        coinCache.set(symbol, null);
      }
    } catch (backendErr) {
      console.warn(
        `Backend fetch failed for ${symbol}, falling back to CoinGecko:`,
        backendErr.message,
      );

      // Fallback to CoinGecko for this symbol
      try {
        const res = await rateLimitedAxiosGet(
          `https://api.coingecko.com/api/v3/coins/markets`,
          {
            params: {
              vs_currency: "usd",
              per_page: 250,
              page: 1,
              sparkline: false,
            },
            timeout: 10000, // 10 second timeout
          },
        );

        const coin = res.data.find((c) => c?.symbol?.toLowerCase() === symbol);
        const image = coin?.image || null;
        results[symbol] = image;
        coinCache.set(symbol, image);
        if (image) {
          saveToLocalStorage(`coin_image_${symbol}`, image);
        }
      } catch (err) {
        console.error(`CoinGecko fallback failed for ${symbol}:`, err.message);
        results[symbol] = null;
        coinCache.set(symbol, null);
      }
    }
  }

  return results;
};

/**
 * Fetches a coin's image URL by its symbol with intelligent batching
 * @param {string} symbol - The coin's ticker symbol
 * @returns {Promise<string|null>} - The URL of the coin's image or null
 */
export const getAssetCoin = async (symbol) => {
  if (!symbol) return null;

  const lowerSymbol = symbol.toLowerCase();
  const cacheKey = `coin_image_${lowerSymbol}`;

  // Check 1: In-memory cache (fastest)
  if (coinCache.has(lowerSymbol)) {
    return coinCache.get(lowerSymbol);
  }

  // Check 2: localStorage cache (persistent)
  const localImage = getFromLocalStorage(cacheKey);
  if (localImage) {
    coinCache.set(lowerSymbol, localImage);
    return localImage;
  }

  // Check if there's already a pending request for this symbol
  if (pendingRequests.has(lowerSymbol)) {
    const existing = pendingRequests.get(lowerSymbol);
    return existing.promise;
  }

  // Create a promise for this symbol
  let resolveFunc;
  const promise = new Promise((resolve) => {
    resolveFunc = resolve;
  });

  // Store the promise and resolve function
  pendingRequests.set(lowerSymbol, { promise, resolve: resolveFunc });

  // Clear existing timeout
  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }

  // Set new timeout to batch requests
  batchTimeout = setTimeout(async () => {
    const symbolsToFetch = Array.from(pendingRequests.keys());
    const entries = Array.from(pendingRequests.values());

    // Clear pending requests
    pendingRequests.clear();

    // Batch fetch
    const results = await batchFetchCoins(symbolsToFetch);

    // Resolve all promises
    symbolsToFetch.forEach((sym, idx) => {
      const resolveFunction = entries[idx].resolve;
      if (typeof resolveFunction === "function") {
        resolveFunction(results[sym] || null);
      }
    });
  }, 100); // Wait 100ms to collect more requests

  return promise;
};

/**
 * Preload coin images for an array of symbols
 * Useful for loading images before they're needed in the UI
 * @param {string[]} symbols - Array of coin symbols to preload
 */
export const preloadCoinImages = async (symbols) => {
  if (!symbols || symbols.length === 0) return;

  const uniqueSymbols = [...new Set(symbols.map((s) => s.toLowerCase()))];
  await batchFetchCoins(uniqueSymbols);
};

/**
 * Clear the coin image cache
 * Useful for forcing a refresh of coin data
 */
export const clearCoinCache = () => {
  coinCache.clear();
  // Clear localStorage cache
  const keys = Object.keys(localStorage);
  keys.forEach((key) => {
    if (key.startsWith("coin_image_")) {
      localStorage.removeItem(key);
    }
  });
};
// export const getAssetName = (token) => {
//   // Return an empty string if the token is not a valid string
//   if (!token || typeof token !== "string") {
//     return "";
//   }

//   // Split, get the first part, and convert to uppercase
//   return token.split(/[/_-]/)[0].toUpperCase();
// };

const QUOTE_CURRENCIES = [
  // Stablecoins / crypto quotes
  "USDT",
  "USDC",
  "BUSD",
  "DAI",
  "BTC",
  "ETH",
  "BNB",

  // Major fiat
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CNY",
  "CHF",
  "AUD",
  "CAD",
  "NZD",

  // Asia
  "SGD",
  "HKD",
  "IDR",
  "MYR",
  "THB",
  "PHP",
  "VND",
  "KRW",
  "INR",

  // Middle East
  "AED",
  "SAR",
  "QAR",
  "KWD",

  // Europe
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "HUF",
  "RON",

  // Latin America
  "BRL",
  "MXN",
  "ARS",
  "CLP",
  "COP",
  "PEN",

  // Africa
  "ZAR",
  "NGN",
  "KES",
  "GHS",
];

export const getAssetName = (token) => {
  if (!token || typeof token !== "string") return "";

  const normalized = token.trim().toUpperCase();

  // Token is itself a currency
  if (QUOTE_CURRENCIES.includes(normalized)) {
    return normalized;
  }

  // BTC/SGD, BTC-SGD, BTC_SGD
  const parts = normalized.split(/[/_-]/);
  if (parts.length > 1) {
    return parts[0];
  }

  // BTCUSDT, ETHSGD, SOLJPY
  for (const quote of QUOTE_CURRENCIES) {
    if (normalized.endsWith(quote)) {
      return normalized.slice(0, -quote.length);
    }
  }

  return normalized;
};

export const normalizePair = (token) => {
  if (!token || typeof token !== "string") return "";

  const normalized = token.trim().toUpperCase();

  //  Already separated: BTC/USDT, BTC-USDT, BTC_USDT
  const splitMatch = normalized.split(/[/_-]/);
  if (splitMatch.length === 2) {
    return `${splitMatch[0]}/${splitMatch[1]}`;
  }

  //  Concatenated: BTCUSDT, ETHIDR, SOLJPY
  for (const quote of QUOTE_CURRENCIES.sort((a, b) => b.length - a.length)) {
    if (normalized.endsWith(quote)) {
      const base = normalized.slice(0, -quote.length);
      if (base) {
        return `${base}/${quote}`;
      }
    }
  }

  //  Fallback (unknown format)
  return normalized;
};

export function normalizeBotName(value) {
  if (!value) return value;

  if (value === "XENO ARC") {
    return "XENOARC";
  }

  return value;
}
